import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableHighlight,
  TouchableOpacity,
  TextInput,
  Pressable,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import {
  getProductsData,
  deleteProduct,
  getProductCategoriesData,
  getProductFamiliesData,
  getProductLinesData,
} from '../../../../api/Product';
import { getLocationsData } from '../../../../api/Settings';
import { msgStr } from '../../../../common/constants/Message';
import { TextMediumSize } from '../../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../../common/hooks';
import BasicLayout from '../../../../common/components/CustomLayout/BasicLayout';

import { productsStyle } from './styles/ProductsStyle';
import BulkUpdateStatus from './BulkUpdateStatus';
import UpdateLocationModal from './UpdateLocationModal';
import TransferHistoryLog from './TransferHistoryLog';


const LocationHistory = ({ navigation, openInventory, data }) => {
  const initialMount = useRef(true);
  const StatusObj = {
    0: 'Ready',
    1: 'Ordered',
    2: 'Checked out',
    3: 'Checked in',
    4: 'Broken',
    5: 'Sold',
    6: 'Transferred',
    7: 'Stolen',
    8: 'Trashed',
  };

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [updateProductTrigger, setUpdateProductsTrigger] = useState(false);
  const [checkedItemIds, setCheckedItemIds] = useState([]);

  const [isUpdateLocationVisible, setUpdateLocationVisible] = useState(false);

  const updateLocationModal = () => { setUpdateLocationVisible(true); }
  const closeUpdateLocationModal = () => { setUpdateLocationVisible(false); }

  const [searchCategory, setSearchCategory] = useState(0);
  const [searchFamily, setSearchFamily] = useState(0);
  const [searchProductLine, setSearchProductLine] = useState(0);
  const [searchBarcode, setSearchBarcode] = useState('');
  const [searchSize, setSearchSize] = useState('');
  const [searchLocation, setSearchLocation] = useState(0);

  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [lines, setLines] = useState([]);
  const [Locations, setLocations] = useState([]);

  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isLoading, setIsLoading] = useState(true);

  const getSortedData = (tableData) => {
    if(sortColumn && sortDirection){
      tableData.sort((a, b) => {
        let A; let B;
        if(sortColumn == 'category'){
          A = a.category.category;  B = b.category.category;
        }else if(sortColumn == 'family'){
          A = a.family.family; B = b.family.family;
        }else if(sortColumn == 'line'){
          A = a.line.line; B = b.line.line;
        }else if(sortColumn == 'size'){
          A = a.line.size; B = b.line.size;
        }else if(sortColumn == 'home_location'){
          A = a.home_location_tbl.location; B = b.home_location_tbl.location;
        }else if(sortColumn == 'current_location'){
          A = a.current_location_tbl.locations.location; B = b.current_location_tbl.locations.location;
        }else{
          A = a[sortColumn];
          B = b[sortColumn];
        }

        if (typeof A === 'number' && typeof B === 'number') {
          return sortDirection === 'asc' ? A - B : B - A;
        } else {
          const textA = String(A).toUpperCase();
          const textB = String(B).toUpperCase();
          return sortDirection === 'asc' ? textA.localeCompare(textB) : textB.localeCompare(textA);
        }
      });

      return tableData;
    }else return tableData;
  }

  const filteredAndSortedData = useMemo(()=>{
    const filteredData = tableData.filter((item) => {
      // const isBarcodeMatch = searchBarcode.trim()
      //   ? item.barcode && item.barcode.toLowerCase().includes(searchBarcode.trim().toLowerCase())
      //   : true;
      const isSizeMatch = searchSize.trim()
        ? item.line && item.line.size.toLowerCase().includes(searchSize.trim().toLowerCase())
        : true;
      const isLocationMatch =
        searchLocation != 0
          ? (item.home_location && item.home_location == searchLocation) ||
            (item.current_location && item.current_location == searchLocation)
          : true;

      return isSizeMatch && isLocationMatch;
    });

    return getSortedData(filteredData);

  }, [tableData, searchSize, searchLocation, sortColumn, sortDirection]);

  useEffect(() => {
    if (data == null && updateProductTrigger == true) {
      getTable();
    }
  }, [updateProductTrigger]);

  useEffect(() => {
    if (initialMount.current == false) {
      let timeoutId = setTimeout(() => {
        getTable();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [searchCategory, searchFamily, searchProductLine]);

  useEffect(() => {
    if (initialMount.current == false) {
      if (searchCategory != 0) {
        loadProductFamiliesData(searchCategory, (jsonRes) => {
          setFamilies(jsonRes);
          if (jsonRes.length > 0) setSearchFamily(jsonRes[0].id);
          else {
            setSearchFamily(0);
          }
        });
      } else {
        setFamilies([]);
        setSearchFamily(0);
      }
    }
  }, [searchCategory]);

  useEffect(() => {
    if (initialMount.current == false) {
      if (searchFamily != 0) {
        loadProductLinesData(searchFamily, (jsonRes) => {
          setLines(jsonRes);
          if (jsonRes.length > 0) setSearchProductLine(jsonRes[0].id);
          else {
            setSearchProductLine(0);
          }
        });
      } else {
        setLines([]);
        setSearchProductLine(0);
      }
    }
  }, [searchFamily]);

  useEffect(() => {
    if (data)
      if (data.searchOptions) {
        setTimeout(() => {
          if (data.searchOptions.categoryId) setSearchCategory(data.searchOptions.categoryId);
          setTimeout(() => {
            if (data.searchOptions.familyId) setSearchFamily(data.searchOptions.familyId);
            setTimeout(() => {
              if (data.searchOptions.lineId) setSearchProductLine(data.searchOptions.lineId);
              data = null;
            }, 50);
          }, 50);
        }, 50);
      }
  }, [data]);

  useEffect(() => {
    loadProductCategoriesData((categories) => {
      const categoryId = categories[0] ? categories[0].id : null;
      loadProductFamiliesData(categoryId, (families) => {
        const familyId = families[0] ? families[0].id : null;
        loadProductLinesData(familyId, (lines) => {
          if (categories.length) {
            setCategories(categories);
            // setSearchCategory(categories[0].id);
          } else setCategories([]);

          if (families.length) {
            setFamilies(families);
            // setSearchFamily(families[0].id);
          } else setFamilies([]);

          if (lines.length) {
            setLines(lines);
            // setSearchProductLine(lines[0].id);
          } else setLines([]);

          setUpdateProductsTrigger(true);
          initialMount.current = false;
        });
      });
    });
    getLocationsData({}, (jsonRes, status, error) => {
      if (status == 200) setLocations(jsonRes);
    });
  }, []);

  const loadProductCategoriesData = (callback) => {
    getProductCategoriesData((jsonRes, status, error) => {
      switch (status) {
        case 200:
          callback(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const loadProductFamiliesData = (categoryId, callback) => {
    getProductFamiliesData(categoryId, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          callback(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const loadProductLinesData = (familyId, callback) => {
    getProductLinesData(familyId, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          callback(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const getTable = () => {
    const products = [...tableData.map(prod => prod.barcode), searchBarcode];
    getProductsData({ barcode: products }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateProductsTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const clearTableData = () => {
    setTableData([]);
  }

  const sortData = (column) =>{
    setIsLoading(true);
    const direction = column === sortColumn && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(direction);
  }

  const renderTableData = () => {
    const rows = [];
    if (filteredAndSortedData.length > 0) {
      filteredAndSortedData.map((item, index) => {
        rows.push(
          <View key={index} style={styles.tableRow}>
            <View style={styles.cell}>
              <Text>{item.barcode ? item.barcode : ''}</Text>
            </View>
            <View style={styles.cell}>
              <Text>{item.category ? item.category.category : ''}</Text>
            </View>
            <View style={[styles.cell, { width: 200 }]}>
              <Text>{item.family ? (item.family.family) : ''}</Text>
            </View>
            <View style={[styles.cell, {width:200}]}>
              <Text>{item.line ? item.line.line + (item.item_starting_number ? ' - ' + item.item_starting_number : '') : ''}</Text>
            </View>
            <View style={[styles.cell, { width: 80 }]}>
              <Text>{item.line ? item.line.size : ''}</Text>
            </View>
            {/* <View style={styles.cell}>
              <Text>{item.serial_number ? item.serial_number : ''}</Text>
            </View> */}
            <View style={styles.cell}>
              <Text>
                {item.home_location_tbl ? item.home_location_tbl.locations.location : ''}
              </Text>
            </View>
            <View style={styles.cell}>
              <Text>
                {item.current_location_tbl ? item.current_location_tbl.locations.location : ''}
              </Text>
            </View>
            <View style={[styles.cell, { width: 110 }]}>
              <Text>{item.status != null ? StatusObj[item.status] : ''}</Text>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  handleProductRemove(item.id);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="trash" color="black" />
              </TouchableOpacity>
            </View>
          </View>
        );
      });
    } else {
      <></>;
    }
    setIsLoading(false);
    return <>{rows}</>;
  };

  const handleBarcodeScan = () => {
    if (!searchBarcode){
      showAlert('Barcode is required');
    }
    const products = [...tableData.map(prod => prod.barcode), searchBarcode];

    getProductsData({ barcode: products }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateProductsTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });

    setSearchBarcode('');
  }

  const handleProductRemove = (id: number) => {
    const products = tableData.filter((product) => product.id !== id);
    setTableData(products);
  }

  const buttonsElement = useMemo(()=>(<View style={styles.toolbar}>
    <Text style={{marginRight:5}}>Bulk Actions</Text>
    <TouchableHighlight style={styles.bulkbutton} onPress={updateLocationModal}>
      <Text style={styles.buttonText}>Update Location</Text>
    </TouchableHighlight>
  </View>), [checkedItemIds]);

    const transferHistoryLogComponent = useMemo(()=>(
      <TransferHistoryLog 
      navigation={navigation}
      openInventory={openInventory}
      data={tableData}
      isChildComponent={true}
      containerStyle={{ paddingLeft: 0, paddingRight: 0, paddingTop: 20 }}
    />), [tableData]);


  const tableElement = useMemo(()=>(<View style={[styles.tableContainer, {maxHeight: 200}]}>
    <View style={styles.tableHeader}>
      <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={()=>sortData('barcode')}>
        <Text>{'Barcode'}</Text>
        {sortColumn == 'barcode' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={()=>sortData('category')}>
        <Text>{'Category'}</Text>
        {sortColumn == 'category' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn, { width: 200 }]} onPress={()=>sortData('family')}>
        <Text>{'Family'}</Text>
        {sortColumn == 'family' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn, {width:200}]} onPress={()=>sortData('line')}>
        <Text>{'Line'}</Text>
        {sortColumn == 'line' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn, { width: 80 }]} onPress={()=>sortData('size')}>
        <Text>{'Size'}</Text>
        {sortColumn == 'size' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={()=>sortData('home_location')}>
        <View>
          <Text>{'Home location'}</Text>
          <Text style={{ fontSize: 10, color: '#dc2626', fontWeight: '600', marginTop: 2 }}>
            (Inventory Count Based)
          </Text>
        </View>
        {sortColumn == 'home_location' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn]} onPress={()=>sortData('current_location')}>
        <Text>{'Current location'}</Text>
        {sortColumn == 'current_location' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      <Pressable style={[styles.columnHeader, styles.sortableColumn, { width: 110 }]} onPress={()=>sortData('status')}>
        <Text>{'Status'}</Text>
        {sortColumn == 'status' && sortDirection == 'desc'?
          <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
          : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
        }
      </Pressable>
      {/* <Text style={[styles.columnHeader, styles.IconCell]}>{'Edit'}</Text> */}
      {/* <Text style={[styles.columnHeader, styles.IconCell]}>{'DEL'}</Text> */}
      <Text style={[styles.columnHeader, styles.IconCell]}>{'Del'}</Text>
    </View>
    <View style={{flex:1}}>
      <ScrollView>
        {renderTableData()}
      </ScrollView>
    </View>
  </View>), [filteredAndSortedData, checkedItemIds])

  const updateLocationModalComponent = useMemo(()=>(
    <UpdateLocationModal
      isModalVisible={isUpdateLocationVisible}
      selectedProducts={tableData}
      setUpdateProductsTrigger={setUpdateProductsTrigger}
      closeModal={closeUpdateLocationModal}
      clearTableData={clearTableData}
    />), [isUpdateLocationVisible, tableData])

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(data?.searchOptions ? 'Product Lines' : null);
      }}
      screenName={'Transfer History'}
      isLoading={isLoading}
    >
      <ScrollView horizontal={true}>
        <View style={styles.container}>
          <View style={styles.toolbar}>
            <View style={[styles.searchBox, { margin: 0 }]}>
              <Text style={[styles.searchLabel, { marginLeft: 0, width: 'auto' }]}>Barcode</Text>
              <TextInput
                style={styles.searchInput}
                placeholder=""
                value={searchBarcode}
                onChangeText={setSearchBarcode}
              />
              <TouchableHighlight style={styles.button} onPress={handleBarcodeScan}>
                <Text style={styles.buttonText}>Scan barcode</Text>
              </TouchableHighlight>
            </View>

            <TouchableHighlight style={[styles.bulkbutton, { display: tableData.length > 0 ? 'flex' : 'none', marginLeft: 20 }]} onPress={updateLocationModal}>
              <Text style={styles.buttonText}>{tableData.length > 0 ? `(${ tableData.length})` : ''} Update Location</Text>
            </TouchableHighlight>
          </View>
          {/* {buttonsElement} */}
          {tableData.length > 0 && tableElement}
          {transferHistoryLogComponent}
           
        </View>
      </ScrollView>
      {updateLocationModalComponent}
    </BasicLayout>
  );
};

const styles = productsStyle;

export default LocationHistory;
